/** The recording screen (R4 on the "Preekie device recorder" canvas; R6 is
 * the same screen at phone width): the clock, a meter per track, and what
 * session A's engine reports -- sharing stopped (R4a), a share of the wrong
 * kind (R4b), no call sound, signed out, not yet uploaded -- plus Stop with
 * #205's confirmation and Discard with its second confirmation. The engine
 * (src/recorder/) does the recording; this file only shows it. */
import { PauseRefusedError, RecorderEngine, ReshareKindError } from "./recorder/engine.js";
import { httpRecorderApi } from "./recorder/api.js";
import type { EngineState, Started, Streams, Unsent, Warning } from "./recorder/types.js";
import { cancelRecorder, currentCsrfToken } from "./api.js";
import {
  ENTIRE_SCREEN_NOTICE,
  LIVE_COPY as L,
  RESHARE_COPY,
  STOP_CONFIRM,
  diagnosticsBody,
  discardCopy,
  displayMediaOptions,
  engineErrorText,
  formatElapsed,
  limitCopy,
  limitState,
  limitsFromStart,
  limitAction,
  RECORD_AGAIN,
  keepAudioOnly,
  liveTitle,
  recorderMediaError,
  reshareVerdict,
  wholeMinutes,
  isSignedOut,
  SIGNED_OUT,
  PAUSE_COPY,
  CONSENT_VERSIONS,
  micConstraints,
} from "./recorder.js";
import type { CallSource, StartState } from "./recorder.js";
import { el } from "./render.js";

export interface RecorderLiveOptions {
  root: HTMLElement;
  streams: Streams;
  started: Started;
  state: StartState; // the start screen's choices and sound check
  clientName: string; // from the signed-in session, never from the device
  timeZone: string;
  onOpenMeeting: (meetingId: string) => void;
  /** A fresh start screen: a new meeting, a new sound check and a new consent tick (a meeting holds one recording). */
  onRecordAgain: () => void;
  /** The uploads after Stop (or Discard) are over: the banner can look again. */
  onSettled: () => void;
  /** #303: the console session ended (a 401 on Discard): sign in, then back to Record a meeting, same client. */
  onSignIn?: () => void;
  /** CAP-009.H11b: offer Pause / Continue (the new console only; today's console doesn't pass it). */
  allowPause?: boolean;
}

export interface LiveRecording {
  readonly meetingId: string;
  /** True from Start until Stop (or Discard) is pressed. */
  recording(): boolean;
  unsentMinutes(): number;
  /** Still uploading or paused on this recording: no second engine may resume it (#223). */
  busyWith(recordingId: string): boolean;
  /** Signing out while recording: stop first; unsent audio stays on the device. */
  stopForSignOut(): Promise<void>;
  /** Moves the live screen into a new container (the record page was opened again). */
  attach(root: HTMLElement): void;
  shutdown(): void;
}

type Panel =
  | { kind: "none" }
  | { kind: "stopped" } // R4a
  | { kind: "refused"; message: string } // R4b, or a window / no-sound share
  | { kind: "silent"; seconds: number };

function button(text: string, cls: string, onClick: () => void): HTMLButtonElement {
  const b = el("button", cls, text);
  b.type = "button";
  b.addEventListener("click", onClick);
  return b;
}

/** A modal with the safe choice focused (Enter or a stray tap keeps things as they are). */
export function confirmDialog(opts: {
  question: string;
  confirm: string;
  cancel: string;
  extra?: HTMLElement;
  onConfirm: () => void;
}): void {
  const dialog = el("dialog", "pk-dialog pk-confirm-dialog");
  dialog.setAttribute("aria-labelledby", "pk-confirm-q");
  const q = el("p", "pk-confirm-question", opts.question);
  q.id = "pk-confirm-q";
  const row = el("div", "pk-dialog-actions");
  const cancel = button(opts.cancel, "btn btn-outline", () => dialog.close());
  const ok = button(opts.confirm, "btn btn-primary", () => {
    dialog.close();
    opts.onConfirm();
  });
  row.append(ok, cancel);
  dialog.append(q);
  if (opts.extra) dialog.append(opts.extra);
  dialog.append(row);
  dialog.addEventListener("close", () => dialog.remove());
  document.body.append(dialog);
  dialog.showModal();
  cancel.focus();
}

/** Discard's second confirmation, naming the minutes; resolves with the choice. */
export function confirmDiscard(minutes: number, onDiscard: (keepUploaded: boolean) => void): void {
  const c = discardCopy(minutes);
  const label = el("label", "pk-check");
  const box = el("input");
  box.type = "checkbox";
  box.checked = true;
  label.append(box, " ", c.keepUploaded);
  confirmDialog({ question: c.question, confirm: c.confirm, cancel: c.cancel, extra: label, onConfirm: () => onDiscard(box.checked) });
}

export function startLiveRecording(opts: RecorderLiveOptions): LiveRecording {
  const s = opts.state;
  const online = s.mode === "online";
  const source: CallSource | null = online ? s.source : null;
  const api = httpRecorderApi(() => currentCsrfToken() ?? "");
  let root = opts.root;
  let engineState: EngineState = "idle";
  let elapsed = 0;
  let callShared = online && opts.streams.call !== null;
  let panel: Panel = { kind: "none" };
  let signedOut = false;
  let storageError = false;
  let uploadsPaused = false;
  // CAP-008.WP11: the cap, from the start response (the server's one setting).
  const limits = limitsFromStart(opts.started as unknown as Record<string, unknown>);
  let limit: "none" | "warning" | "stopped" = "none";
  let unsent: Unsent | null = null;
  let stopPressed = false;
  let error: string | null = null;
  let offerSignIn = false; // #303: the error is SIGNED_OUT, shown with a Sign in button
  let sharing = false;
  let levels: { mic: number; call: number | null } = { mic: 0, call: null };
  const consentAt = new Date();
  // CAP-009.H11b: the streams in use now (fresh ones after each Continue), the pause's end, and why it ended.
  let current: Streams = opts.streams;
  let pausedUntil: Date | null = null;
  let pauseExpired = false;
  let continuing = false;

  const engine = new RecorderEngine(
    {
      onState: (st) => {
        engineState = st;
        draw();
      },
      onLevels: (l) => {
        levels = l;
        applyLevels();
      },
      onElapsed: (ms) => {
        elapsed = ms;
        const clock = root.querySelector<HTMLElement>("[data-clock]");
        if (clock) clock.textContent = formatElapsed(ms);
        onLimit(limitState(ms, limits));
      },
      onWarning: (w: Warning) => onWarning(w),
      onUnsent: (u) => setUnsent(u),
      onError: (code, message) => {
        if (code === "reshare_kind") return; // shown as R4b by shareAgain()
        error = engineErrorText(code, message);
        draw();
      },
    },
    { api, getCsrf: () => currentCsrfToken() ?? "" }
  );

  function onWarning(w: Warning): void {
    // Session A's #230: a refused upload is a warning, once per episode; the next stored chunk ends it.
    // Read by name so this works before and after that engine change.
    // CAP-008.WP11 (#263): the engine also watches the cap and stops itself at the limit. Whichever fires first,
    // the screen shows the same states; read by name so this builds before and after #263's types land.
    const kind = (w as { kind: string }).kind;
    if (kind === "recording_limit") return onLimit("warning");
    if (kind === "recording_limit_reached") return onLimit("stopped");
    if (kind === "pause_unsaved" || kind === "pause_saved") return draw();
    if (kind === "pause_expired") {
      pauseExpired = true;
      pausedUntil = null;
      draw();
      return;
    }
    if (kind === "uploads_paused") {
      uploadsPaused = true;
      draw();
      return;
    }
    if (w.kind === "call_stopped") {
      callShared = false;
      panel = { kind: "stopped" };
    } else if (w.kind === "call_silent") {
      if (panel.kind === "none") panel = { kind: "silent", seconds: w.seconds };
    } else if (w.kind === "signed_out") signedOut = true;
    else if (w.kind === "storage_error") storageError = true;
    else if (w.kind === "unsent") return setUnsent(w.unsent);
    else if (w.kind === "expiring") {
      /* shown by the unsent line's 24-hour wording */
    }
    draw();
  }

  /** The engine reports unsent audio every tick: redraw only when what's shown changes. */
  function setUnsent(u: Unsent | null): void {
    const shown = (x: Unsent | null) => (x ? `${wholeMinutes(x.minutes)}|${x.warn_24h}` : "");
    let changed = shown(u) !== shown(unsent);
    // A chunk stored since the pause began ends it.
    // (Any drop in the waiting count means one was stored, even while new ones are recorded.)
    if (uploadsPaused && (u === null || (unsent !== null && u.chunks < unsent.chunks))) {
      uploadsPaused = false;
      changed = true;
    }
    unsent = u;
    if (changed) draw();
  }

  function applyLevels(): void {
    setMeter("mic", levels.mic);
    setMeter("call", callShared ? (levels.call ?? 0) : 0);
  }

  function setMeter(track: "mic" | "call", v: number): void {
    const bar = root.querySelector<HTMLElement>(`[data-live-meter="${track}"] > div`);
    if (!bar) return;
    const pct = Math.round(Math.max(0, Math.min(1, v * 4)) * 100);
    bar.style.width = `${pct}%`;
    bar.parentElement?.setAttribute("aria-valuenow", String(pct));
  }

  async function shareAgain(): Promise<void> {
    if (sharing || !source) return;
    sharing = true;
    error = null;
    try {
      const display = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions(source) as DisplayMediaStreamOptions);
      const shared = keepAudioOnly(display);
      const verdict = reshareVerdict(source, shared);
      if (!verdict.ok) {
        display.getTracks().forEach((t) => t.stop());
        panel = { kind: "refused", message: verdict.message };
      } else {
        try {
          engine.replaceCall(new MediaStream(display.getAudioTracks()), shared.surface);
          callShared = true;
          panel = { kind: "none" };
        } catch (err) {
          display.getTracks().forEach((t) => t.stop());
          panel = { kind: "refused", message: err instanceof ReshareKindError ? RESHARE_COPY[source].otherKind : recorderMediaError(err) };
        }
      }
    } catch (err) {
      // Cancelling the picker keeps R4a as it was.
      if ((err as { name?: string } | null)?.name !== "NotAllowedError") error = recorderMediaError(err);
    }
    sharing = false;
    draw();
  }

  function stop(): void {
    confirmDialog({
      question: STOP_CONFIRM.question,
      confirm: STOP_CONFIRM.stop,
      cancel: STOP_CONFIRM.keep,
      onConfirm: () => void doStop(),
    });
  }

  /** Warn once at the warning time; at the limit, stop cleanly with no question asked. */
  function onLimit(next: "none" | "warning" | "stopped"): void {
    // Without the server's numbers there's no cap to show (the engine may still stop on its own fallback).
    const step = limitAction(limit, next, limits !== null, stopPressed);
    if (step.act === "none") return;
    limit = step.state;
    if (step.act === "stop") {
      // A Stop question still open would ask about a recording that has already stopped.
      document.querySelectorAll<HTMLDialogElement>("dialog.pk-confirm-dialog[open]").forEach((d) => d.close());
      void doStop();
      return;
    }
    draw();
  }

  async function doStop(): Promise<void> {
    if (stopPressed) return;
    stopPressed = true;
    panel = { kind: "none" };
    draw();
    // Before Stop: the server revokes the recording's token once it's complete
    // (seen on test, 18 Oct: a 401 after). The engine's current token, not Start's (#223).
    await engine.sendDiagnostics(diagnosticsBody("ended", null, navigator.userAgent, null));
    try {
      await engine.stop();
    } catch (err) {
      error = recorderMediaError(err);
    }
    stopTracks();
    draw();
    void engine.finished().then(() => {
      draw();
      opts.onSettled();
    });
  }

  function discard(): void {
    // The upload may have caught up since the button was drawn (session D).
    if (wholeMinutes(unsent?.minutes ?? 0) === 0) {
      unsent = null;
      draw();
      return;
    }
    confirmDiscard(unsent?.minutes ?? 0, (keepUploaded) => {
      void engine
        .discard({ keepUploaded })
        .then(() => {
          stopPressed = true;
          stopTracks();
          draw();
          opts.onSettled();
        })
        .catch((err: unknown) => {
          // Signed out: after Stop, offer Sign in here; while recording, never leave the page (sign in in another tab).
          offerSignIn = isSignedOut(err) && stopPressed;
          error = !isSignedOut(err)
            ? "The audio wasn't discarded. Check your connection and try again."
            : stopPressed
              ? SIGNED_OUT.message
              : SIGNED_OUT.whileRecording;
          draw();
        });
    });
  }

  function stopTracks(): void {
    current.mic.getTracks().forEach((t) => t.stop());
    current.call?.getTracks().forEach((t) => t.stop());
  }

  // ---- Pause / Continue (CAP-009.H11b) ----

  async function pause(): Promise<void> {
    error = null;
    try {
      await engine.pause(); // flushes, stops capture, releases the mic and the share, tells the server
      pausedUntil = new Date(Date.now() + 30 * 60_000);
    } catch (err) {
      error = err instanceof PauseRefusedError && err.code === "too_many_pauses" ? PAUSE_COPY.tooMany : (err as Error).message;
    }
    draw();
  }

  /** Before every Continue (RJ Legal, Prabash 2026-10-24): has anyone joined during the pause? "Read consent message again" shows the
   * full message above the same question and buttons; only the dialog's Continue recording (a tap) continues. No new tick: the consent
   * given at the start covers the whole meeting (D). */
  function continueCheck(): void {
    if (continuing || document.querySelector("dialog.pk-continue-check")) return;
    const dialog = el("dialog", "pk-dialog pk-confirm-dialog pk-continue-check");
    dialog.setAttribute("aria-labelledby", "pk-continue-q");
    const message = el("blockquote", "pk-notice-quote pk-record-notice pk-continue-message", CONSENT_VERSIONS.withPause.notice);
    message.hidden = true;
    message.tabIndex = -1;
    const q = el("p", "pk-confirm-question", PAUSE_COPY.checkQuestion);
    q.id = "pk-continue-q";
    const row = el("div", "pk-dialog-actions");
    const go = button(PAUSE_COPY.continue, "btn btn-primary pk-continue-go", () => {
      dialog.close();
      void continueRecording();
    });
    const again = button(PAUSE_COPY.readAgain, "btn btn-outline pk-continue-read", () => {
      message.hidden = false;
      message.focus();
    });
    row.append(go, again);
    dialog.append(message, q, row);
    dialog.addEventListener("close", () => dialog.remove());
    document.body.append(dialog);
    dialog.showModal();
    again.focus();
  }

  /** Only from the person's tap: a fresh microphone and, online, the same kind of share as before. */
  async function continueRecording(): Promise<void> {
    if (continuing) return;
    continuing = true;
    error = null;
    draw();
    let mic: MediaStream | null = null;
    let display: MediaStream | null = null;
    try {
      mic = await navigator.mediaDevices.getUserMedia({ audio: micConstraints(s.mode) });
      let call: MediaStream | null = null;
      let surface: string | null = null;
      if (online && source) {
        display = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions(source) as DisplayMediaStreamOptions);
        const shared = keepAudioOnly(display);
        const verdict = reshareVerdict(source, shared);
        if (!verdict.ok) throw new Error(verdict.message);
        call = new MediaStream(display.getAudioTracks());
        surface = shared.surface;
      }
      await engine.continueRecording({ mic, call }, { call_surface: surface });
      current = { mic, call };
      callShared = !!call;
      pausedUntil = null;
      panel = { kind: "none" };
    } catch (err) {
      mic?.getTracks().forEach((t) => t.stop());
      display?.getTracks().forEach((t) => t.stop());
      if (err instanceof PauseRefusedError && err.code === "pause_expired") error = null; // shown as the ended state
      else if ((err as { name?: string } | null)?.name === "NotAllowedError") error = PAUSE_COPY.reshare;
      else if (err instanceof ReshareKindError && source) error = RESHARE_COPY[source].otherKind;
      else error = err instanceof Error ? err.message : recorderMediaError(err);
    }
    continuing = false;
    draw();
  }

  // D: a lock, sleep or leaving the page while paused ends the recording (it is never continued without a fresh start).
  const onHidden = () => {
    if (document.visibilityState === "hidden" && engine.isPaused()) void engine.endPause();
  };
  document.addEventListener("visibilitychange", onHidden);
  window.addEventListener("pagehide", onHidden);

  async function resume(): Promise<void> {
    try {
      await engine.resumeAfterSignIn();
      signedOut = false;
      void engine.sendDiagnostics(diagnosticsBody("resumed", null, navigator.userAgent, null));
    } catch {
      error = "Preekie couldn't resume the upload. Make sure you're signed in, then try again.";
    }
    draw();
  }

  function alertBox(cls: string, role: "alert" | "status", ...children: Array<Node | string>): HTMLElement {
    const box = el("div", cls);
    box.setAttribute("role", role);
    box.append(...children);
    return box;
  }

  function pausedView(): HTMLElement {
    const page = el("div", "pk-live pk-live-paused");
    const head = el("div", "pk-live-head");
    const left = el("div");
    left.append(el("div", "muted pk-live-where", opts.clientName));
    const title = el("div", "pk-live-title pk-live-paused-title");
    title.setAttribute("role", "status");
    title.append(el("span", undefined, engineState === "pausing" ? PAUSE_COPY.pausing : engine.pauseSaved() ? PAUSE_COPY.paused : PAUSE_COPY.pausedUnsaved));
    left.append(title);
    const clock = el("div", "figure pk-live-clock", formatElapsed(elapsed));
    clock.dataset.clock = "";
    clock.setAttribute("aria-label", "Since the recording started");
    head.append(left, clock);
    page.append(head, el("p", "pk-live-paused-text", PAUSE_COPY.nothingRecorded));
    if (engineState === "paused" && !engine.pauseSaved()) page.append(el("p", "muted", PAUSE_COPY.unsavedNote));
    if (pausedUntil) {
      const at = pausedUntil.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: opts.timeZone }).replace(/\s/g, " ").toLowerCase();
      page.append(el("p", "muted", PAUSE_COPY.endsAt(at)));
    }
    if (online && source) page.append(el("p", "muted", PAUSE_COPY.reshare));
    if (error) page.append(alertBox("pk-alert", "alert", error));
    const row = el("div", "pk-live-actions pk-live-paused-actions");
    const go = button(continuing ? PAUSE_COPY.continuing : PAUSE_COPY.continue, "btn btn-primary pk-live-continue", () => continueCheck());
    go.disabled = continuing || engineState === "pausing";
    const stopBtn = button(L.stopRecording, "btn btn-outline pk-live-stop", stop);
    stopBtn.disabled = engineState === "pausing";
    row.append(go, stopBtn);
    page.append(row);
    return page;
  }

  function draw(): void {
    const pausedNow = opts.allowPause && !stopPressed && !pauseExpired && (engineState === "paused" || engineState === "pausing");
    // The check asks about a pause: once there isn't one (continued, ended, stopped), it closes.
    if (!pausedNow) document.querySelector<HTMLDialogElement>("dialog.pk-continue-check")?.close();
    if (pausedNow) {
      root.replaceChildren(pausedView());
      return;
    }
    const recording = !stopPressed && !pauseExpired && (engineState === "recording" || engineState === "starting" || engineState === "idle");
    const page = el("div", "pk-live");
    const head = el("div", "pk-live-head");
    const left = el("div");
    const where = online ? `Online call · ${source === "screen" ? "Entire Screen" : "Browser tab"}` : "In person";
    left.append(el("div", "muted pk-live-where", `${where} · ${opts.clientName}`));
    const title = el("div", "pk-live-title");
    title.setAttribute("role", "status");
    if (recording) {
      const dot = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      dot.setAttribute("width", "16");
      dot.setAttribute("height", "16");
      dot.setAttribute("viewBox", "0 0 16 16");
      dot.setAttribute("aria-hidden", "true");
      dot.classList.add("pk-live-dot");
      const c = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      c.setAttribute("cx", "8");
      c.setAttribute("cy", "8");
      c.setAttribute("r", "7");
      dot.append(c);
      title.append(dot, el("span", undefined, liveTitle(s.mode, source, callShared)));
    } else title.append(el("span", undefined, engineState === "done" ? "Stopped" : L.stopping));
    left.append(title);
    const clock = el("div", "figure pk-live-clock", formatElapsed(elapsed));
    clock.dataset.clock = "";
    clock.setAttribute("aria-label", "Recorded so far");
    head.append(left, clock);
    page.append(head);

    if (recording && source === "screen" && callShared) {
      const n = el("p", "pk-entire-screen-notice", ENTIRE_SCREEN_NOTICE);
      n.setAttribute("role", "note");
      page.append(n);
    }

    if (recording && limits && limit === "warning") {
      const w = el("div", "pk-notice pk-live-notice pk-live-limit");
      w.setAttribute("role", "alert");
      w.dataset.pkRecordingLimit = "warning";
      w.textContent = limitCopy(limits).warning;
      page.append(w);
    }

    if (recording && source && (panel.kind === "stopped" || panel.kind === "refused")) {
      const copy = RESHARE_COPY[source];
      const body = el("div", "pk-live-alert-body");
      body.append(el("p", "pk-live-alert-text", panel.kind === "stopped" ? copy.stopped : panel.message));
      if (source === "screen") body.append(el("p", "pk-entire-screen-notice", ENTIRE_SCREEN_NOTICE));
      const again = button(sharing ? "Waiting for you to choose…" : copy.button, "btn btn-primary", () => void shareAgain());
      again.disabled = sharing;
      body.append(again, el("p", "pk-live-alert-note", copy.switchKind));
      page.append(alertBox("pk-alert pk-live-alert", "alert", body));
    } else if (recording && panel.kind === "silent") {
      page.append(alertBox("pk-notice pk-live-notice", "status", L.callSilent(Math.max(1, Math.round(panel.seconds / 60)))));
    }
    if (signedOut) {
      const body = el("div", "pk-live-alert-body");
      body.append(el("p", "pk-live-alert-text", L.signedOut), button(L.resume, "btn btn-primary", () => void resume()));
      page.append(alertBox("pk-alert pk-live-alert", "alert", body));
    }
    if (storageError) page.append(alertBox("pk-alert", "alert", L.storage));
    if (uploadsPaused) page.append(alertBox("pk-notice pk-live-notice", "status", L.uploadsPaused));
    if (error && offerSignIn && opts.onSignIn) {
      const box = alertBox("pk-alert pk-record-signed-out", "alert", el("p", undefined, error), button(SIGNED_OUT.action, "btn btn-primary", opts.onSignIn));
      box.dataset.pkSignedOut = "";
      page.append(box);
    } else if (error) page.append(alertBox("pk-alert", "alert", error));

    // Meters, R4: You, and the call (or all computer sound).
    const sound = el("section", "pk-live-sound");
    sound.setAttribute("aria-label", "Sound");
    sound.append(meterRow("mic", L.you, L.microphone, true));
    if (online) sound.append(meterRow("call", source === "screen" ? L.allSound : L.call, callShared ? (source === "screen" ? "This computer" : "Call sound") : L.notShared, callShared));
    if (recording) page.append(sound);

    const mins = wholeMinutes(unsent?.minutes ?? 0);
    if (unsent && mins > 0) {
      const line = el("div", unsent.warn_24h ? "pk-live-unsent pk-live-unsent-warn" : "pk-live-unsent");
      line.setAttribute("role", "status");
      line.append(
        el("span", undefined, `${mins} minute${mins === 1 ? "" : "s"} not yet uploaded${unsent.warn_24h ? ", deleted from this computer within 24 hours" : ""}. Keep this tab open.`),
        button("Discard…", "btn btn-outline pk-btn-small", discard)
      );
      page.append(line);
    }

    if (recording) {
      const consent = el("p", "muted pk-live-consent", "Consent confirmed at ");
      consent.append(el("span", "figure", consentAt.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: opts.timeZone }).replace(/\s/g, " ").toLowerCase()));
      page.append(consent);
      const row = el("div", "pk-live-actions");
      const alerting = panel.kind === "stopped" || panel.kind === "refused";
      if (opts.allowPause) {
        const p = button(PAUSE_COPY.pause, "btn btn-outline pk-live-pause", () => void pause());
        p.disabled = engineState !== "recording";
        row.append(p);
      }
      row.append(button(L.stopRecording, alerting ? "btn btn-outline pk-live-stop" : "btn btn-primary pk-live-stop", stop));
      page.append(row);
    } else {
      // R7, briefly: where the audio is; the meeting page shows the notes' progress.
      const done = el("section", "pk-live-done");
      const text =
        engineState === "quarantined" ? L.quarantined : engineState === "done" ? L.done : L.uploading;
      if (limits && limit === "stopped") {
        const auto = el("p", "pk-live-limit-stopped", limitCopy(limits).stopped);
        auto.setAttribute("role", "status");
        auto.dataset.pkRecordingLimit = "stopped";
        done.append(auto);
      }
      if (pauseExpired) {
        const ended = el("p", "pk-live-limit-stopped", PAUSE_COPY.expired);
        ended.setAttribute("role", "status");
        done.append(ended);
      }
      done.append(el("p", undefined, text));
      if (engineState === "done") {
        const row = el("div", "pk-actions");
        row.append(
          button(L.openNotes, "btn btn-primary", () => opts.onOpenMeeting(opts.started.meeting_id)),
          button(RECORD_AGAIN, "btn btn-outline", () => opts.onRecordAgain())
        );
        done.append(row);
      }
      page.append(done);
    }
    root.replaceChildren(page);
    applyLevels();
  }

  function meterRow(track: "mic" | "call", name: string, sub: string, live: boolean): HTMLElement {
    const row = el("div", "pk-meter-row");
    const who = el("div", "pk-meter-who");
    who.append(el("span", undefined, name));
    const meter = el("div", live ? "pk-meter" : "pk-meter pk-meter-off");
    meter.dataset.liveMeter = track;
    meter.setAttribute("role", "meter");
    meter.setAttribute("aria-label", `${name} level`);
    meter.setAttribute("aria-valuemin", "0");
    meter.setAttribute("aria-valuemax", "100");
    meter.setAttribute("aria-valuenow", "0");
    meter.append(el("div"));
    row.append(who, meter, el("span", live ? "pk-meter-wait" : "pk-meter-off-label", sub));
    return row;
  }

  // While recording, leaving the page asks first (the browser's own prompt).
  const beforeUnload = (ev: BeforeUnloadEvent) => {
    if (!stopPressed) {
      ev.preventDefault();
      ev.returnValue = "";
    }
  };
  window.addEventListener("beforeunload", beforeUnload);

  draw();
  void engine
    .start(opts.streams, opts.started, { call_source: source, call_surface: s.surface })
    .then(() =>
      engine.sendDiagnostics(diagnosticsBody("started", null, navigator.userAgent, s))
    )
    .catch((err) => {
      error = recorderMediaError(err);
      stopPressed = true;
      stopTracks();
      draw();
      // Nothing was recorded: close it, or it would block the next Start (it isn't in the server's unsent list).
      void cancelRecorder(opts.started.recording_id).catch(() => undefined);
    });

  return {
    meetingId: opts.started.meeting_id,
    recording: () => !stopPressed,
    busyWith: (id) => engine.busyWith(id),
    unsentMinutes: () => unsent?.minutes ?? 0,
    async stopForSignOut() {
      await doStop();
    },
    attach(next: HTMLElement) {
      root = next;
      draw();
    },
    shutdown() {
      window.removeEventListener("beforeunload", beforeUnload);
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("pagehide", onHidden);
      engine.shutdown();
    },
  };
}
